<script setup lang="ts">
import { Address } from '@/application/shared/domain/Address';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';
import { dateHelpers } from '@hfc/ui-core';
import { ADDRESS_TYPE } from '@hfc/common/src/types/AddressTypes';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionAddress from '@/application/shared/infrastructure/components/questions/QuestionAddress.vue';
import { ref } from 'vue';

const { fromTimestampToFormatted } = dateHelpers();

const props = defineProps<{
  applicationType: AppTypeDentalOrVision;
}>();

const {
  dentalAndVisionApplicationStore,
  notPrimaryApplicantsAddressDifferentThanPrimary
} = useDentalAndVisionApplication(props.applicationType);

interface emitInterface {
  (event: 'setLoading', value: boolean): void;
}

const emits = defineEmits<emitInterface>();

const mainAddressRef = ref<InstanceType<typeof QuestionAddress> | null>(null);

const setMainAddressValue = (value: Partial<Address>, index: number): void => {
  const applicant = notPrimaryApplicantsAddressDifferentThanPrimary.value[index];
  dentalAndVisionApplicationStore.updateApplicant({ main_address: { ...applicant.main_address as Address, ...value, type: ADDRESS_TYPE.HOME } }, props.applicationType, applicant.main_index);
};

const sectionSubmit = async (): Promise<boolean> => {
  if (mainAddressRef.value && Array.isArray(mainAddressRef.value)) {
    const results = await Promise.all(mainAddressRef.value.map(ref => ref?.handleSubmit()));

    return results.every(result => result === true);
  }

  return false;
};

defineExpose({
  sectionSubmit: sectionSubmit
});
</script>
<template>
  <BaseSection title="Dependents' Addresses">
    <div v-for="(applicant, i) in notPrimaryApplicantsAddressDifferentThanPrimary" :key="applicant.main_index" class="py-4">
      <div class="flex gap-14 text-gray-600 text-lg my-2">
        <p>{{ applicant.first_name }} {{ applicant.last_name }} ({{ fromTimestampToFormatted(applicant.date_of_birth, 'MM/DD/YYYY') }})</p>
      </div>
      <QuestionAddress :value="applicant.main_address" @update:value="(v: Partial<Address>) => setMainAddressValue(v, i)" ref="mainAddressRef" :id="`dependent-address-${applicant.main_index}`" :application-type="applicationType" :validate-coverage="true" @set-loading="val => emits('setLoading', val)" />
    </div>
  </BaseSection>
</template>
